<script setup>
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, onUnmounted, reactive, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useCalls } from "../../Composables/useCalls.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import _ from 'lodash'
import axios from "axios"
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"
import FormSelectField from "../../Components/FormSelectField.vue"
import Plyr from 'plyr'
import { useRoute, useRouter } from 'vue-router'
import 'plyr/dist/plyr.css'

const { httpErrorsHandler } = useHttpErrorsHandler()
const route = useRoute()
const router = useRouter()
const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)

const storageKey = "Shiftech_CallList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: calls,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    search,
    loadData,
} = useDataManager({
    baseUrl: '/api/calls',
    baseSearchUrl: '/api/search/calls',
    defaultSortingField: 'start_time',
    defaultSortingFieldsOrder: {
        start_time: 'desc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: 'Type', slot: 'type', class: 'col-1 text-center' },
    { label: 'Numéro', slot: 'number', class: 'col-1' },
    { label: 'Client', slot: 'client', class: 'col-1' },
    { label: 'Centre', slot: 'center', class: 'col-1' },
    { label: 'Date', slot: 'date', class: 'col-1' },
    { label: 'Durée', slot: 'duration', class: 'col-1' },
    { label: 'Enregistrement', slot: 'recording', class: 'col-2 ps-4', cellClass: 'ps-4 py-0' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

const errors = ref(null)
const filters = reactive({
    type: null,
    center_id: null,
})

const callTypes = [
    { text: 'Tous les appels', value: null },
    { text: 'À rappeler (7 derniers jours)', value: 'to_call_back' },
    { text: '---', value: null },
    { text: 'Manqués', value: 'missed' },
    { text: 'Messagerie', value: 'voicemail' },
    { text: 'Entrants décrochés', value: 'answered' },
    { text: 'Sortants', value: 'outgoing' },
]
const filtersApplied = ref(true)
const isInitialized = ref(false)
const centers = ref([])

onBeforeMount(() => {
    getCenters()
    subscribeToCallChannel()

    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        // Adresse mémorisée invalide (ancien bug du lien « À rappeler » : /null) : on repart de l'adresse par défaut
        url.value = savedSettings.url?.includes('/api/') ? savedSettings.url : url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        filtersApplied.value = savedSettings.filtersApplied ?? true

        if (savedSettings.filters) {
            Object.assign(filters, savedSettings.filters)
        }
    }

    // Lien direct vers une liste filtrée (ex. « À rappeler » depuis le tableau de bord)
    if (route.query.type) {
        filters.type = route.query.type
        router.replace({ query: {} })
        applyFilter()
        return
    }

    loadData(url.value)
})

onMounted(() => {
    isInitialized.value = true
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded, filtersApplied, filters],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded, newFiltersApplied]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            filtersApplied: newFiltersApplied,
            filters: { ...filters },
        })
    },
    { deep: true }
)

watch(
    () => [filters.center_id, filters.type],
    () => {
        if (isInitialized.value) {
            filtersApplied.value = false
        }
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

function applyFilter() {
    let newUrl = new URL(url.value, window.location.origin)
    // Nouveau filtre : on repart de la première page
    newUrl.searchParams.delete('page')

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== '') {
            newUrl.searchParams.set(key, value)
        } else {
            newUrl.searchParams.delete(key)
        }
    })

    filtersApplied.value = true
    loadData(newUrl.href)
}

function getCenters() {
    centers.value.push({
        text: 'Tous les centres',
        value: null
    })
    axios.get('/api/centers?sortingField=name&sortingOrder=asc&all==true')
        .then(response => {
            response.data.data.forEach(center => {
                centers.value.push({
                    text: center.data.name,
                    value: center.data.id
                })
            })
        })
        .catch(error => {
            httpErrorsHandler(error)
        })
}

const idToDelete = ref(null)

function showModal(modalId, id) {
    idToDelete.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    deleteCall,
} = useCalls({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onError: (error) => httpErrorsHandler(error),
})

// Call type badge logic
function defineCallTypeBadge(call) {
    if (call.data.direction === 'out') {
        return 'out'
    }
    if (call.data.direction === 'in' && call.data.last_state === 'missed') {
        return 'missed'
    }
    if (call.data.direction === 'in' && (call.data.last_state === 'voicemail' || call.data.voicemail_link !== null)) {
        return 'voicemail'
    }
    if (call.data.direction === 'in' && (call.data.last_state === 'hangup' || call.data.last_state === 'answered' || call.data.last_state === 'ringing')) {
        return 'in'
    }
}

// Plyr audio players
let players = []
watch(loading, async () => {
    if (loading.value === false) {
        await nextTick()
        players.forEach(player => {
            if (player) {
                player.destroy()
            }
        })
        players = Array.from(document.querySelectorAll('.players')).map(p => new Plyr(p, {
            controls: ['play', 'progress'],
            volume: 1,
            displayDuration: false,
        }))
    }
})

onUnmounted(() => {
    players.forEach(player => {
        if (player) {
            player.destroy()
        }
    })
})

// Echo channel subscriptions for real-time updates
function subscribeToCallChannel() {
    if (authUser.value.role === "admin") {
        Echo.private('calls_all')
            .listen('CallCreatedEvent', (event) => {
                if (filters.type !== null || (filters.center_id !== null && filters.center_id !== event.call.data.center_id)) {
                    return
                }
                calls.value.unshift(event.call)
                calls.value.pop()
            })
            .listen('CallUpdatedEvent', (event) => {
                const index = calls.value.findIndex(call => call.data.id === event.call.data.id)
                if (index !== -1) {
                    calls.value[index] = event.call
                }
            })
    } else {
        authUser.value.centers.forEach(center => {
            Echo.private('calls_center_' + center.id)
                .listen('CallCreatedEvent', (event) => {
                    if (filters.type !== null || (filters.center_id !== null && filters.center_id !== event.call.data.center_id)) {
                        return
                    }
                    calls.value.unshift(event.call)
                    calls.value.pop()
                })
                .listen('CallUpdatedEvent', (event) => {
                    const index = calls.value.findIndex(call => call.data.id === event.call.data.id)
                    if (index !== -1) {
                        calls.value[index] = event.call
                    }
                })
        })
    }
}

onUnmounted(() => {
    if (authUser.value.role === "admin") {
        Echo.leave('calls_all')
    } else {
        authUser.value.centers.forEach(center => {
            Echo.leave('calls_center_' + center.id)
        })
    }
})

// Call details modal
const callToDisplay = ref(null)
function displayCallDetails(call) {
    callToDisplay.value = call
    const modalCallDetails = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalCallDetails'))
    modalCallDetails.show()
}
</script>

<template>
    <div class="row">
        <div class="col-lg-12 px-0 px-md-2">
            <div class="card">
                <div class="card-header">
                    <form>
                        <div class="row g-3">
                            <div class="col-xxl col-sm-12">
                                <div class="search-box">
                                    <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchCalls">
                                    <i class="ri-search-line search-icon"></i>
                                </div>
                            </div>
                            <form-select-field
                                name="filters.type"
                                label="Type"
                                :label-hidden="true"
                                @update:field="filters.type = $event"
                                :errors="errors"
                                :required="true"
                                :data="filters.type"
                                bs-class="col-xxl-3 col-sm-6"
                                :disabled="false"
                                additional-class="bg-light border-light"
                                placeholder="Tous les appels"
                                :liste-options="callTypes"
                                margin="mb-0"
                            />
                            <form-select-field
                                name="filters.center_id"
                                label="Centre"
                                :label-hidden="true"
                                @update:field="filters.center_id = $event"
                                :errors="errors"
                                :required="true"
                                :data="filters.center_id"
                                bs-class="col-xxl-3 col-sm-6"
                                :disabled="false"
                                additional-class="bg-light border-light"
                                placeholder="Tous les centres"
                                :liste-options="centers"
                                margin="mb-0"
                            />
                            <div class="col-xxl-auto col-6">
                                <button type="button" class="btn w-100" @click.prevent="applyFilter" :class="filtersApplied ? 'btn-dark' : 'btn-warning'">
                                    <span class="text-nowrap"><i class="ri-equalizer-fill me-1 align-bottom"></i>Filtrer</span>
                                </button>
                            </div>
                            <div class="col-xxl-auto col-6">
                                <button type="button" class="btn btn-secondary w-100" @click.prevent="applyFilter">
                                    <span class="text-nowrap"><i class="ri-refresh-line me-1 align-bottom"></i>Rafraichir</span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="calls"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @page-changed="loadData"
                    >
                        <!-- Type -->
                        <template #type="{ item }">
                            <span class="badge fs-11 bg-danger-subtle text-danger" v-if="defineCallTypeBadge(item) === 'missed'">Manqué <span v-if="item.data.missed_call_count > 0"> ({{ item.data.missed_call_count }})</span></span>
                            <span class="badge fs-11 bg-warning-subtle text-warning" v-if="defineCallTypeBadge(item) === 'voicemail'">Messagerie</span>
                            <span class="badge fs-11 bg-secondary-subtle text-secondary" v-if="defineCallTypeBadge(item) === 'out'">Sortant</span>
                            <span class="badge fs-11 bg-success-subtle text-success" v-if="defineCallTypeBadge(item) === 'in'">Entrant</span>
                        </template>

                        <!-- Number -->
                        <template #number="{ item }">
                            <i class="mdi mdi-phone-outgoing me-2 fs-20 align-middle text-secondary" v-if="defineCallTypeBadge(item) === 'out'"></i>
                            <i class="mdi mdi-phone-incoming me-2 fs-20 align-middle text-success" v-if="defineCallTypeBadge(item) === 'in'"></i>
                            <i class="mdi mdi-phone-missed me-2 fs-20 align-middle text-danger" v-if="defineCallTypeBadge(item) === 'missed'"></i>
                            <i class="mdi mdi-voicemail me-2 fs-20 align-middle text-warning" v-if="defineCallTypeBadge(item) === 'voicemail'"></i>
                            <span class="fw-semibold text-dark">{{ item.data.client.data.phone_number }}</span>
                        </template>

                        <!-- Client -->
                        <template #client="{ item }">
                            <router-link :to="{ name: 'clients.show', params: { id: item.data.client.data.id }}" class="mr-1 align-middle text-secondary" title="Afficher la fiche client">
                                {{ item.data.client.data.last_name }} {{ item.data.client.data.first_name }}
                            </router-link>
                            <router-link :to="{ name: 'clients.edit', params: { id: item.data.client.data.id } }" class="text-info align-middle ms-1" title="Modifier le client" v-if="item.data.client.can.update">
                                <i class="ri-pencil-fill fs-17 px-1 align-middle"></i>
                            </router-link>
                        </template>

                        <!-- Center -->
                        <template #center="{ item }">
                            {{ item.data.center.data.name }}
                        </template>

                        <!-- Date -->
                        <template #date="{ item }">
                            {{ item.data.start_time_fr }}
                        </template>

                        <!-- Duration -->
                        <template #duration="{ item }">
                            {{ item.data.total_duration }}
                        </template>

                        <!-- Recording -->
                        <template #recording="{ item }">
                            <audio class="players" v-if="item.data.record_link !== null" preload="none">
                                <source :src="item.data.record_link" type="audio/mp3">
                                Your browser does not support the audio element.
                            </audio>
                            <audio class="players" v-if="item.data.voicemail_link !== null">
                                <source :src="item.data.voicemail_link" type="audio/mp3">
                                Your browser does not support the audio element.
                            </audio>
                        </template>

                        <!-- Details -->
                        <template #details="{ item }">
                            <button type="button" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm" @click.prevent="displayCallDetails(item)">Détails</button>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- Call -->
                            <a :href="'tel:' + item.data.client.data.phone_number.replace(/\s+/g, '')" class="text-info" title="Appeler">
                                <i class="ri-phone-fill fs-17 px-1"></i>
                            </a>
                            <!-- DELETE -->
                            <a href="#" class="text-danger" title="Supprimer l'appel" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                        </template>
                    </data-table>

                    <modal id="modalDelete" title="Supprimer l'appel ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteCall(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous \u00eates sur le point de supprimer un appel.</p>
                        </template>
                    </modal>

                    <!-- Modal call details -->
                    <div class="modal fade zoomIn" id="modalCallDetails" tabindex="-1" aria-hidden="true">
                        <div class="modal-dialog">
                            <div class="modal-content" v-if="callToDisplay !== null">
                                <div class="modal-header">
                                    <h5 class="modal-title">Appel {{ callToDisplay.data.direction === 'in' ? 'du' : 'vers le' }} <span class="text-muted fw-normal">{{ callToDisplay.data.client.data.phone_number }}</span></h5>
                                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                                </div>
                                <div class="modal-body">
                                    <div class="table-responsive">
                                        <table class="table table-nowrap mb-3">
                                            <tbody>
                                            <tr>
                                                <th scope="row" class="col-4">Type</th>
                                                <td>
                                                    <span class="badge fs-11 bg-danger-subtle text-danger" v-if="defineCallTypeBadge(callToDisplay) === 'missed'">Appel manqué <span v-if="callToDisplay.data.missed_call_count > 0"> ({{ callToDisplay.data.missed_call_count }})</span></span>
                                                    <span class="badge fs-11 bg-warning-subtle text-warning" v-if="defineCallTypeBadge(callToDisplay) === 'voicemail'">Messagerie</span>
                                                    <span class="badge fs-11 bg-secondary-subtle text-secondary" v-if="defineCallTypeBadge(callToDisplay) === 'out'">Appel sortant</span>
                                                    <span class="badge fs-11 bg-success-subtle text-success" v-if="defineCallTypeBadge(callToDisplay) === 'in'">Appel entrant</span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th scope="row" class="col-4">Centre</th>
                                                <td>{{ callToDisplay.data.center.data.name }}</td>
                                            </tr>
                                            <tr>
                                                <th scope="row" class="col-4">Client</th>
                                                <td class="align-middle">
                                                    <router-link :to="{ name: 'clients.show', params: { id: callToDisplay.data.client.data.id }}" class="mr-1 align-middle text-secondary" title="Afficher la fiche client">
                                                        {{ callToDisplay.data.client.data.last_name }} {{ callToDisplay.data.client.data.first_name }}
                                                    </router-link>
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <!-- Voicemail -->
                                    <div class="profile-timeline" v-if="defineCallTypeBadge(callToDisplay) === 'voicemail'">
                                        <div class="accordion accordion-flush" id="accordionFlushExample">
                                            <div class="accordion-item border-0">
                                                <div class="accordion-item border-0" v-for="(missedCall, index) in callToDisplay.data.missed_call_time" :key="index">
                                                    <div class="accordion-header" id="headingOne">
                                                        <a class="accordion-button p-2 shadow-none">
                                                            <div class="d-flex align-items-center">
                                                                <div class="flex-shrink-0 avatar-xs">
                                                                    <div class="avatar-title rounded-circle shadow" :class="(index + 1) < callToDisplay.data.missed_call_time.length ? 'bg-danger' : 'bg-warning'">
                                                                        <i class="mdi mdi-phone-missed-outline"></i>
                                                                    </div>
                                                                </div>
                                                                <div class="flex-grow-1 ms-3">
                                                                    <h6 class="fs-15 mb-0 fw-semibold">Appel manqué - <span class="fw-normal text-muted">{{ missedCall }}</span></h6>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </div>
                                                </div>
                                                <div id="collapseOne" class="accordion-collapse collapse show">
                                                    <div class="accordion-body ms-2 ps-5 pt-0">
                                                        <p class="mb-0 pt-3 align-middle"><i class="mdi mdi-voicemail me-2 align-middle fs-16"></i>Nouveau message vocal</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="accordion-item border-0">
                                                <div class="accordion-header" id="headingOne">
                                                    <a class="accordion-button p-2 shadow-none">
                                                        <div class="d-flex align-items-center">
                                                            <div class="flex-shrink-0 avatar-xs">
                                                                <div class="avatar-title bg-dark rounded-circle shadow">
                                                                    <i class="mdi mdi-phone-off"></i>
                                                                </div>
                                                            </div>
                                                            <div class="flex-grow-1 ms-3">
                                                                <h6 class="fs-15 mb-0 fw-semibold">Appel terminé - <span class="fw-normal text-muted">{{ callToDisplay.data.end_time_fr }}</span></h6>
                                                            </div>
                                                        </div>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Missed -->
                                    <div class="profile-timeline" v-if="defineCallTypeBadge(callToDisplay) === 'missed'">
                                        <div class="accordion accordion-flush" id="accordionFlushExample">
                                            <div class="accordion-item border-0" v-for="(missedCall, index) in callToDisplay.data.missed_call_time" :key="index">
                                                <div class="accordion-header" id="headingOne">
                                                    <a class="accordion-button p-2 shadow-none">
                                                        <div class="d-flex align-items-center">
                                                            <div class="flex-shrink-0 avatar-xs">
                                                                <div class="avatar-title bg-danger rounded-circle shadow">
                                                                    <i class="mdi mdi-phone-missed-outline"></i>
                                                                </div>
                                                            </div>
                                                            <div class="flex-grow-1 ms-3">
                                                                <h6 class="fs-15 mb-0 fw-semibold">Appel manqué - <span class="fw-normal text-muted">{{ missedCall }}</span></h6>
                                                            </div>
                                                        </div>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Out -->
                                    <div class="profile-timeline" v-if="defineCallTypeBadge(callToDisplay) === 'out'">
                                        <div class="accordion accordion-flush" id="accordionFlushExample">
                                            <div class="accordion-item border-0">
                                                <div class="accordion-header" id="headingOne">
                                                    <a class="accordion-button p-2 shadow-none">
                                                        <div class="d-flex align-items-center">
                                                            <div class="flex-shrink-0 avatar-xs">
                                                                <div class="avatar-title bg-secondary rounded-circle shadow">
                                                                    <i class="mdi mdi-phone-outgoing"></i>
                                                                </div>
                                                            </div>
                                                            <div class="flex-grow-1 ms-3">
                                                                <h6 class="fs-15 mb-0 fw-semibold">Appel sortant - <span class="fw-normal text-muted">{{ callToDisplay.data.start_time_fr }}</span></h6>
                                                            </div>
                                                        </div>
                                                    </a>
                                                </div>
                                                <div id="collapseOne" class="accordion-collapse collapse show">
                                                    <div class="accordion-body ms-2 ps-5 pt-0">
                                                        <p class="mb-0 pt-3 align-middle"><i class="bx bx-time-five me-1 align-middle fs-16"></i>Durée : {{ callToDisplay.data.total_duration }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="accordion-item border-0">
                                                <div class="accordion-header" id="headingOne">
                                                    <a class="accordion-button p-2 shadow-none">
                                                        <div class="d-flex align-items-center">
                                                            <div class="flex-shrink-0 avatar-xs">
                                                                <div class="avatar-title bg-dark rounded-circle shadow">
                                                                    <i class="mdi mdi-phone-off"></i>
                                                                </div>
                                                            </div>
                                                            <div class="flex-grow-1 ms-3">
                                                                <h6 class="fs-15 mb-0 fw-semibold">Appel terminé - <span class="fw-normal text-muted">{{ callToDisplay.data.end_time_fr }}</span></h6>
                                                            </div>
                                                        </div>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- In -->
                                    <div class="profile-timeline" v-if="defineCallTypeBadge(callToDisplay) === 'in'">
                                        <div class="accordion accordion-flush" id="accordionFlushExample">
                                            <!-- Call hanged up -->
                                            <div class="accordion-item border-0" v-if="callToDisplay.data.missed_call_count === 0">
                                                <div class="accordion-header" id="headingOne">
                                                    <a class="accordion-button p-2 shadow-none">
                                                        <div class="d-flex align-items-center">
                                                            <div class="flex-shrink-0 avatar-xs">
                                                                <div class="avatar-title bg-success rounded-circle shadow">
                                                                    <i class="mdi mdi-phone-incoming"></i>
                                                                </div>
                                                            </div>
                                                            <div class="flex-grow-1 ms-3">
                                                                <h6 class="fs-15 mb-0 fw-semibold">Appel entrant - <span class="fw-normal text-muted">{{ callToDisplay.data.start_time_fr }}</span></h6>
                                                            </div>
                                                        </div>
                                                    </a>
                                                </div>
                                                <div id="collapseOne" class="accordion-collapse collapse show">
                                                    <div class="accordion-body ms-2 ps-5 pt-0">
                                                        <p class="mb-0 pt-3 align-middle"><i class="bx bx-time-five me-1 align-middle fs-16"></i>Durée : {{ callToDisplay.data.total_duration }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- Missed call -->
                                            <div class="accordion-item border-0" v-for="(missedCall, index) in callToDisplay.data.missed_call_time" :key="index" v-else>
                                                <div class="accordion-header" id="headingOne">
                                                    <a class="accordion-button p-2 shadow-none">
                                                        <div class="d-flex align-items-center">
                                                            <div class="flex-shrink-0 avatar-xs">
                                                                <div class="avatar-title rounded-circle shadow bg-danger">
                                                                    <i class="mdi mdi-phone-missed-outline"></i>
                                                                </div>
                                                            </div>
                                                            <div class="flex-grow-1 ms-3">
                                                                <h6 class="fs-15 mb-0 fw-semibold">Appel manqué - <span class="fw-normal text-muted">{{ missedCall }}</span></h6>
                                                            </div>
                                                        </div>
                                                    </a>
                                                </div>
                                            </div>
                                            <!-- Call answered -->
                                            <div class="accordion-item border-0" v-if="callToDisplay.data.answered_time_fr !== null && callToDisplay.data.missed_call_count > 0">
                                                <div class="accordion-header" id="headingOne">
                                                    <a class="accordion-button p-2 shadow-none">
                                                        <div class="d-flex align-items-center">
                                                            <div class="flex-shrink-0 avatar-xs">
                                                                <div class="avatar-title bg-success rounded-circle shadow">
                                                                    <i class="mdi mdi-phone-incoming"></i>
                                                                </div>
                                                            </div>
                                                            <div class="flex-grow-1 ms-3">
                                                                <h6 class="fs-15 mb-0 fw-semibold">Appel entrant - <span class="fw-normal text-muted">{{ callToDisplay.data.answered_time_fr }}</span></h6>
                                                            </div>
                                                        </div>
                                                    </a>
                                                </div>
                                                <div id="collapseOne" class="accordion-collapse collapse show">
                                                    <div class="accordion-body ms-2 ps-5 pt-0">
                                                        <p class="mb-0 pt-3 align-middle"><i class="bx bx-time-five me-1 align-middle fs-16"></i>Durée : {{ callToDisplay.data.total_duration }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- Call ended -->
                                            <div class="accordion-item border-0">
                                                <div class="accordion-header" id="headingOne">
                                                    <a class="accordion-button p-2 shadow-none">
                                                        <div class="d-flex align-items-center">
                                                            <div class="flex-shrink-0 avatar-xs">
                                                                <div class="avatar-title bg-dark rounded-circle shadow">
                                                                    <i class="mdi mdi-phone-off"></i>
                                                                </div>
                                                            </div>
                                                            <div class="flex-grow-1 ms-3">
                                                                <h6 class="fs-15 mb-0 fw-semibold">Appel terminé - <span class="fw-normal text-muted">{{ callToDisplay.data.end_time_fr }}</span></h6>
                                                            </div>
                                                        </div>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="modal-footer">
                                    <button type="button" class="btn btn-soft-dark" data-bs-dismiss="modal">Fermer</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
